'use client';

import { Children, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import { cx } from '@/shared/lib/cx';
import { Icon } from '@/shared/ui/icon';

import styles from './scroll-row.module.scss';

export type ScrollRowProps = {
  heading: ReactNode;
  children: ReactNode;
  prevLabel: string;
  nextLabel: string;
  /** "square" — editorial arrows; "round" — 01B arrows. */
  arrows?: 'square' | 'round';
  className?: string | undefined;
  listLabel?: string;
};

/** Horizontal list with swipe/scroll-snap and prev/next arrows next to the heading. */
export function ScrollRow({
  heading,
  children,
  prevLabel,
  nextLabel,
  arrows = 'square',
  className,
  listLabel,
}: ScrollRowProps) {
  const trackRef = useRef<HTMLUListElement>(null);
  const [edges, setEdges] = useState({ start: true, end: false });
  const update = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth;
    setEdges({ start: track.scrollLeft <= 2, end: track.scrollLeft >= max - 2 });
  }, []);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;
    const frame = requestAnimationFrame(update);
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      cancelAnimationFrame(frame);
      track.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [update]);
  const scroll = (direction: 1 | -1) => {
    const track = trackRef.current;
    const item = track?.firstElementChild;
    if (!track || !(item instanceof HTMLElement)) return;
    const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 0;
    track.scrollBy({ left: direction * (item.offsetWidth + gap), behavior: 'smooth' });
  };
  return (
    <div className={cx(styles.root, className)}>
      <div className={styles.head}>
        <div className={styles.heading}>{heading}</div>
        <div className={styles.controls}>
          <button
            type="button"
            className={styles.arrow}
            data-shape={arrows}
            aria-label={prevLabel}
            disabled={edges.start}
            onClick={() => scroll(-1)}
          >
            <Icon
              name={arrows === 'round' ? 'arrow-left' : 'chevron-left'}
              size={arrows === 'round' ? 18 : 20}
              strokeWidth={arrows === 'round' ? 1.3 : 1.4}
            />
          </button>
          <button
            type="button"
            className={styles.arrow}
            data-shape={arrows}
            aria-label={nextLabel}
            disabled={edges.end}
            onClick={() => scroll(1)}
          >
            <Icon
              name={arrows === 'round' ? 'arrow-right' : 'chevron-right'}
              size={arrows === 'round' ? 18 : 20}
              strokeWidth={arrows === 'round' ? 1.3 : 1.4}
            />
          </button>
        </div>
      </div>
      <ul ref={trackRef} className={styles.track} aria-label={listLabel}>
        {Children.map(children, (child) => (
          <li className={styles.item}>{child}</li>
        ))}
      </ul>
    </div>
  );
}
